import { useApiClient } from '@yuksalish/api-client'
import { agentRoleList } from '../types/procurement'
import type {
  AgentOption,
  CurrencyOption,
  PagedResult,
  ProductOption,
  ProductUom,
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderReceipt,
  ReceivePurchaseOrderPayload,
  SavePurchaseOrderPayload,
  SaveSupplierInvoicePayload,
  SupplierInvoice,
  SupplierInvoiceFilters,
} from '../types/procurement'

interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

interface ApiItem<T> {
  data: T
}

function parseMeta(meta: ApiList<never>['meta'], fallbackPage = 1, fallbackPerPage = 20) {
  return {
    page: meta?.page ?? meta?.current_page ?? fallbackPage,
    per_page: meta?.per_page ?? fallbackPerPage,
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

/**
 * Procurement data layer.
 *
 * `GET /purchase-orders` is paged server-side (`search`, `page`, `per_page`,
 * `MAX_PER_PAGE = 100`) and returns a `meta` block. `GET /supplier-invoices`
 * still returns the full matching set without a `meta` block, so invoice lists
 * are filtered/paginated client-side. Option lists (suppliers, warehouses,
 * currencies, products) come from the shared Agents/Products modules and need
 * `agents.view` / `products.view`.
 */
export function useProcurementApi() {
  const client = useApiClient()

  async function listPurchaseOrders(filters: PurchaseOrderFilters = {}): Promise<PagedResult<PurchaseOrder>> {
    const page = filters.page ?? 1
    const perPage = filters.per_page ?? 20

    const response = await client.get<ApiList<PurchaseOrder>>('/purchase-orders', {
      params: {
        search: filters.search || undefined,
        status: filters.status || undefined,
        supplier_agent_id: filters.supplier_agent_id || undefined,
        destination_warehouse_agent_id: filters.destination_warehouse_agent_id || undefined,
        requester_user_id: filters.requester_user_id || undefined,
        currency_code: filters.currency_code || undefined,
        page,
        per_page: perPage,
      },
    })

    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, page, perPage),
    }
  }

  async function getPurchaseOrder(id: number): Promise<PurchaseOrder> {
    const response = await client.get<ApiItem<PurchaseOrder>>(`/purchase-orders/${id}`)
    return response.data.data
  }

  async function createPurchaseOrder(payload: SavePurchaseOrderPayload): Promise<PurchaseOrder> {
    const response = await client.post<ApiItem<PurchaseOrder>>('/purchase-orders', payload)
    return response.data.data
  }

  async function updatePurchaseOrder(id: number, payload: SavePurchaseOrderPayload): Promise<PurchaseOrder> {
    const response = await client.put<ApiItem<PurchaseOrder>>(`/purchase-orders/${id}`, payload)
    return response.data.data
  }

  async function receivePurchaseOrder(id: number, payload: ReceivePurchaseOrderPayload): Promise<PurchaseOrder> {
    const response = await client.post<ApiItem<PurchaseOrder>>(`/purchase-orders/${id}/receive`, payload)
    return response.data.data
  }

  /** `GET /purchase-orders/{id}/receipts` — inbound purchase movements. */
  async function listPurchaseOrderReceipts(id: number): Promise<PurchaseOrderReceipt[]> {
    const response = await client.get<ApiList<PurchaseOrderReceipt>>(`/purchase-orders/${id}/receipts`)
    return response.data.data ?? []
  }

  async function deletePurchaseOrder(id: number): Promise<void> {
    await client.delete(`/purchase-orders/${id}`)
  }

  /** One page of agents for a role, with optional server-side search. */
  async function listAgentOptions(filters: {
    role: string
    search?: string
    /**
     * Optional active filter. The pickers omit it so every counterparty of the
     * role is findable; the filter dropdowns pass `1` for active-only.
     */
    is_active?: 0 | 1
    page?: number
    per_page?: number
  }): Promise<PagedResult<AgentOption>> {
    const response = await client.get<ApiList<AgentOption>>('/agents', {
      params: {
        role: filters.role,
        is_active: filters.is_active,
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })

    return {
      items: (response.data.data ?? []).map((agent) => ({
        id: Number(agent.id),
        name: agent.name,
        phone: agent.phone ?? null,
        roles: agentRoleList(agent.roles),
      })),
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function listSuppliers(): Promise<AgentOption[]> {
    const result = await listAgentOptions({ role: 'supplier', is_active: 1, per_page: 500 })
    return result.items
  }

  async function listWarehouses(): Promise<AgentOption[]> {
    const result = await listAgentOptions({ role: 'warehouse', is_active: 1, per_page: 500 })
    return result.items
  }

  async function listDepartments(): Promise<AgentOption[]> {
    const result = await listAgentOptions({ role: 'department', is_active: 1, per_page: 500 })
    return result.items
  }

  async function listCurrencies(): Promise<CurrencyOption[]> {
    const response = await client.get<ApiList<CurrencyOption>>('/currencies', {
      params: { page: 1, per_page: 200 },
    })

    return (response.data.data ?? []).filter((currency) => currency.is_active !== 0 && currency.is_active !== false)
  }

  async function listProducts(filters: {
    type?: string
    search?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<ProductOption>> {
    const response = await client.get<ApiList<ProductOption>>('/products', {
      params: {
        type: filters.type || undefined,
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })

    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  /** `GET /products/{id}/uoms` — the product base unit plus its configured UOM set. */
  async function getProductUoms(productId: number): Promise<{ unit: string | null; items: ProductUom[] }> {
    const response = await client.get<
      ApiItem<{ product_id: number; product_name: string; unit: string | null; items: ProductUom[] }>
    >(`/products/${productId}/uoms`)

    return {
      unit: response.data.data.unit ?? null,
      items: response.data.data.items ?? [],
    }
  }

  /**
   * `GET /supplier-invoices` — supplier payables.
   *
   * The endpoint returns the whole matching set (no `meta` block and no
   * `search` filter), so the list page filters and paginates client-side.
   */
  async function listSupplierInvoices(filters: SupplierInvoiceFilters = {}): Promise<SupplierInvoice[]> {
    const response = await client.get<ApiList<SupplierInvoice>>('/supplier-invoices', {
      params: {
        supplier_agent_id: filters.supplier_agent_id || undefined,
        status: filters.status || undefined,
        currency_code: filters.currency_code || undefined,
      },
    })

    return response.data.data ?? []
  }

  async function getSupplierInvoice(id: number): Promise<SupplierInvoice> {
    const response = await client.get<ApiItem<SupplierInvoice>>(`/supplier-invoices/${id}`)
    return response.data.data
  }

  async function createSupplierInvoice(payload: SaveSupplierInvoicePayload): Promise<SupplierInvoice> {
    const response = await client.post<ApiItem<SupplierInvoice>>('/supplier-invoices', payload)
    return response.data.data
  }

  async function updateSupplierInvoice(
    id: number,
    payload: SaveSupplierInvoicePayload,
  ): Promise<SupplierInvoice> {
    const response = await client.put<ApiItem<SupplierInvoice>>(`/supplier-invoices/${id}`, payload)
    return response.data.data
  }

  async function deleteSupplierInvoice(id: number): Promise<void> {
    await client.delete(`/supplier-invoices/${id}`)
  }

  return {
    listPurchaseOrders,
    getPurchaseOrder,
    createPurchaseOrder,
    updatePurchaseOrder,
    receivePurchaseOrder,
    listPurchaseOrderReceipts,
    deletePurchaseOrder,
    listAgentOptions,
    listSuppliers,
    listWarehouses,
    listDepartments,
    listCurrencies,
    listProducts,
    getProductUoms,
    listSupplierInvoices,
    getSupplierInvoice,
    createSupplierInvoice,
    updateSupplierInvoice,
    deleteSupplierInvoice,
  }
}
